import { formatAmount } from '@hq/utils';
import { Tooltip, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useAuthStore, type HqProfile } from '../stores/auth';

/** 金额不可见时的占位：不暴露位数与量级（字段级脱敏）。 */
export const AMOUNT_MASK = '****';

/** 可见金额所需的权限码：结算/统计口径天然能看金额。 */
const AMOUNT_PERMS = ['wl:settle:manage', 'wl:analytics:view'];

/**
 * 金额可见性（RBAC + 最小必要）：
 * 管理员与 L1 及以上可见；L0（普通申领员/医生/护士）需额外具备结算或统计权限。
 * 仅前端遮挡，后端仍按接口返回；如要求「接口级」脱敏需在服务端下发 mask 标记。
 */
export function canViewAmount(profile: HqProfile | null): boolean {
  if (!profile) return false;
  if (profile.level === 'admin') return true;
  if (profile.level !== 'L0') return true;
  return (profile.perms ?? []).some((perm) => AMOUNT_PERMS.includes(perm));
}

export function useCanViewAmount(): boolean {
  return canViewAmount(useAuthStore((state) => state.profile));
}

interface MoneyTextProps {
  value: number | null | undefined;
  precision?: number;
  prefix?: ReactNode;
  /** 覆盖默认样式（如统计卡、强调色）。 */
  style?: React.CSSProperties;
}

/** 金额文本：不可见时返回 ****，并提示「金额按权限隐藏」。 */
export function MoneyText({ value, precision = 2, prefix, style }: MoneyTextProps) {
  const visible = useCanViewAmount();
  if (!visible) {
    return (
      <Tooltip title="金额按账号权限隐藏">
        <Typography.Text type="secondary" style={style}>
          {AMOUNT_MASK}
        </Typography.Text>
      </Tooltip>
    );
  }
  return (
    <span style={style}>
      {prefix}
      {formatAmount(value ?? 0, precision)}
    </span>
  );
}
